import React, { useEffect, useRef } from 'react';
import {
    ClipboardDocumentIcon,
    ArrowUpTrayIcon,
    ArrowDownTrayIcon,
} from '../../../automationStudioIcons';
import styles from '../../../../../styles/automationStudio/stepContextMenu.module.scss';

const COPY_DISABLED_REASON = 'Only custom steps can be copied';

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/i.test(navigator.platform);
const MOD = isMac ? '⌘' : 'Ctrl+';

const SHORTCUT = {
    copy: isMac ? `${MOD}C` : `${MOD}C`,
    pasteAbove: isMac ? `${MOD}⇧V` : `${MOD}Shift+V`,
    pasteBelow: isMac ? `${MOD}V` : `${MOD}V`,
};

function MenuRow({
    icon: Icon,
    label,
    shortcut,
    disabled,
    disabledReason,
    onClick,
}) {
    const classNames = [styles.item, disabled ? styles.itemDisabled : ''].filter(Boolean).join(' ');
    const handleClick = () => { if (!disabled) onClick(); };
    return (
        <div
            role="menuitem"
            tabIndex={disabled ? -1 : 0}
            aria-disabled={disabled || undefined}
            className={classNames}
            onClick={handleClick}
            onKeyDown={(e) => {
                if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    onClick();
                }
            }}
        >
            <div className={styles.itemRow}>
                <Icon className={styles.itemIcon} />
                <span className={styles.itemLabel}>{label}</span>
                <span className={styles.itemShortcut}>{shortcut}</span>
            </div>
            {disabled && disabledReason && (
                <div className={styles.itemSubtitle}>{disabledReason}</div>
            )}
        </div>
    );
}

export default function StepContextMenu({
    anchorPosition,
    isOpen,
    onClose,
    canCopy,
    canPaste,
    onCopy,
    onPasteAbove,
    onPasteBelow,
}) {
    const menuRef = useRef(null);

    useEffect(() => {
        if (!isOpen) return;
        const onKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
        const onDocPointer = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) onClose();
        };
        document.addEventListener('keydown', onKeyDown);
        document.addEventListener('mousedown', onDocPointer);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.removeEventListener('mousedown', onDocPointer);
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const style = anchorPosition
        ? { top: anchorPosition.top, left: anchorPosition.left }
        : undefined;

    const close = (cb) => () => { cb(); onClose(); };

    return (
        <div
            ref={menuRef}
            role="menu"
            aria-label="Step actions"
            className={styles.menu}
            style={style}
            onContextMenu={(e) => e.preventDefault()}
        >
            <MenuRow
                icon={ClipboardDocumentIcon}
                label="Copy step"
                shortcut={SHORTCUT.copy}
                disabled={!canCopy}
                disabledReason={COPY_DISABLED_REASON}
                onClick={close(onCopy)}
            />
            <div className={styles.divider} role="separator" />
            <MenuRow
                icon={ArrowUpTrayIcon}
                label="Paste above"
                shortcut={SHORTCUT.pasteAbove}
                disabled={!canPaste}
                onClick={close(onPasteAbove)}
            />
            <MenuRow
                icon={ArrowDownTrayIcon}
                label="Paste below"
                shortcut={SHORTCUT.pasteBelow}
                disabled={!canPaste}
                onClick={close(onPasteBelow)}
            />
        </div>
    );
}
